import { useArticleStore } from '~/store/article';
import { useCommonStore } from '~/store/common';
import { useProfileLoginStore } from '~/store/profileLogin';
import { type Article } from '~/types/article';
import { getOneId } from '~/utils/utils';

function getLoginStatus (): string {
  return getOneId() !== null ? 'logged-in' : 'not-logged-in';
}

function shouldUseDNKeyValue (pubName: string): boolean {
  const publications = ['dn', 'd2', 'magasinet'];
  return publications.includes(pubName.toLowerCase());
}

function getSection (pubName: string, categories: any[], articleAdConfig: any) {
  if (!shouldUseDNKeyValue(pubName)) { return 'articlepage'; }
  if (pubName !== 'dn') { return pubName; }

  const sections: string[] = [];
  const allowedSections = articleAdConfig?.targetSections || [];

  if (!!categories && categories.length > 0) {
    for (let i = 0; i < categories.length; i++) {
      if (allowedSections.includes(categories[i].id)) {
        sections.push(categories[i].id);
      }
    }
  }
  if (sections.length === 0) {
    sections.push('dn_other');
  }
  return sections;
}

const dnTargets = (pubName: string, currentArticle: Article) => {
  const commonStore = useCommonStore();
  const isAppView = commonStore.isAppView;
  const targets = {} as any;
  targets.dn_topic = currentArticle?.topic?.label || '';
  targets.dn_keyword = currentArticle?.adKeywords || [];
  targets.dn_cookieinfo = window.nhst && window.nhst.cookieMessageShown && !window.nhst.shouldCheckIplogin ? 'Lukk' : '';
  if (isAppView) {
    targets.app_name = pubName;
  }
  if (navigator.userAgent.includes('DNApp') && (navigator.userAgent.includes('Android') || navigator.userAgent.includes('iOS'))) {
    targets.app_environment = navigator.userAgent.includes('Android') ? 'Android' : 'iOS';
  }
  return targets;
};

const globalTargets = (host: string, targetPrefix: string, currentArticle: Article) => {
  const targets = {} as any;
  const mainCategory = currentArticle?.categories ? currentArticle.categories.find(c => c.default) : false;
  targets[`${targetPrefix}_article_section`] = (mainCategory?.id || 'news').toLowerCase();
  if (currentArticle?.tags) {
    const terms = [];
    const schemes = [];
    const maxTagIterator = currentArticle.tags.length > 5 ? 5 : currentArticle.tags.length;
    for (let i = 0; i < maxTagIterator; i++) {
      terms.push(currentArticle.tags[i].label);
      schemes.push(`tag:${currentArticle.tags[i].type.toLowerCase()}@${host}`);
    }
    targets[`${targetPrefix}_tag_scheme`] = schemes;
    targets[`${targetPrefix}_tag_term`] = terms;
  }
  return targets;
};

function sendPageLevelTargeting (host: string, articleAdConfig: any, currentArticle: Article) {
  const {
    appEnv,
    ad: { pubName, targetPrefix }
  } = useRuntimeConfig().public;
  const paywallVisible = currentArticle?.body?.filter(i => !i.injected).length < currentArticle.bodyLength;
  const isSubscriber = useProfileLoginStore().loginStatus.isSubscriber;

  const envKey = shouldUseDNKeyValue(pubName) ? 'mode' : 'ads_env';
  const loginKey = shouldUseDNKeyValue(pubName) ? 'dn_loginstatus' : 'user_login';
  const loginValue = shouldUseDNKeyValue(pubName) ? getLoginStatus() : `${getOneId() !== null}`;
  const sectionValue = getSection(pubName, currentArticle?.categories, articleAdConfig);
  const abTestEnabled = articleAdConfig?.abTestEnabled;

  window.googletag.pubads().setTargeting(envKey, appEnv);
  window.googletag.pubads().setTargeting(loginKey, loginValue);
  window.googletag.pubads().setTargeting('paywall.user', paywallVisible ? (isSubscriber ? 'subscriber' : 'unsubscribed') : 'false');
  window.googletag.pubads().setTargeting(`${targetPrefix}_section`, sectionValue);
  window.googletag.pubads().setTargeting('dn_article_id', currentArticle?.id || '');
  window.googletag.pubads().setTargeting('cpp', 'true');
  window.googletag.pubads().setTargeting('dn_testads', `${getTestAdStatus()}`);

  const screenWidthTargetRange = getScreenWidthTargetRange();
  if (screenWidthTargetRange) {
    window.googletag.pubads().setTargeting('screen_width_range', screenWidthTargetRange);
  }

  if (abTestEnabled === true) {
    window.googletag.pubads().setTargeting('dn_cookietest', getABCookie() === 'A' ? 'a50' : 'b50');
  }

  if (currentArticle.type === 'featureArticle') {
    const adAlgorithm = currentArticle?.metadata?.manuallyPlacedAds === true ? 'manual' : 'automatic';
    window.googletag.pubads().setTargeting('ad_algorithm', adAlgorithm);
  }

  const targets = shouldUseDNKeyValue(pubName) ? dnTargets(pubName, currentArticle) : globalTargets(host, targetPrefix, currentArticle);
  Object.entries(targets).forEach(([key, value]) => {
    window.googletag.pubads().setTargeting(key, value);
  });

  if (pubName !== 'dn') {
    const personalizedAd = typeof window.OptanonActiveGroups !== 'undefined' && window.OptanonActiveGroups && window.OptanonActiveGroups.match(/,C0001,/) ? 0 : 1;
    window.googletag.pubads().setRequestNonPersonalizedAds(personalizedAd);
    (window.adsbygoogle = window.adsbygoogle || []).requestNonPersonalizedAds = personalizedAd;
  }
}

function sendSlotLevelTargeting (articleAdConfig: any, currentArticle: Article) {
  const pushDownSticky = articleAdConfig?.pushDownSticky;
  const theme = currentArticle?.metadata?.theme;
  const dataLayout = currentArticle?.dataLayout;

  const topBanner: any = articleAdConfig.static?.topBanner;
  if (shouldShowAd(topBanner)) {
    const wallpaperCompliant = !pushDownSticky?.themes?.includes(theme) &&
      !pushDownSticky?.dataLayouts?.some((layout: string) => dataLayout?.includes(layout));
    const keywords = {
      wallpaper_compliant: `${wallpaperCompliant}`
    };
    window.lwhb.adServerKeywordsAdUnit(topBanner.id, keywords);
  }

  const inlineAds: any[] = articleAdConfig.inline || [];
  inlineAds.forEach((ad: any) => {
    if (ad.type === 'ad' && ad.enabled && ad.renderLogic) {
      const deviceLogic = ad.renderLogic[getDeviceType()];
      if (deviceLogic && deviceLogic.minContentWeightAbove) {
        const keywords = {
          dn_character_count: deviceLogic.minContentWeightAbove
        };
        window.lwhb.adServerKeywordsAdUnit(ad.id, keywords);
      }
    }
  });
}

export const useAdTargetingArticle = (host: string, articleAdConfig: any) => {
  if (import.meta.client) {
    const currentArticle = useArticleStore().currentArticle;
    if (window?.googletag?.cmd) {
      window.googletag.cmd.push(function () {
        sendPageLevelTargeting(host, articleAdConfig, currentArticle);
      });
    }
    if (window?.lwhb) {
      window.lwhb.cmd.push(function () {
        sendSlotLevelTargeting(articleAdConfig, currentArticle);
      });
    }
  }
};
